/**
 * R5 — useNormalizationQueue hook.
 *
 * Walks the voci array, collects all pending _inference_hints, and exposes
 * a FIFO queue. The consumer renders <NormalizationConfirmModal> for the
 * head of the queue and calls dequeue() after each confirmation.
 *
 * When `session.voci` changes (e.g. after confirm-inference patch), the
 * queue is automatically re-derived.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

export default function useNormalizationQueue(session) {
  const [cursor, setCursor] = useState(0);
  const [dismissed, setDismissed] = useState(() => new Set());

  // Build queue from all voci hints
  const queue = useMemo(() => {
    const out = [];
    (session?.voci || []).forEach((v, idx) => {
      (v._inference_hints || []).forEach((hint) => {
        const key = `${idx}:${hint.field}`;
        if (dismissed.has(key)) return;
        out.push({ voce: v, voceIdx: idx, hint, key });
      });
    });
    return out;
  }, [session?.voci, dismissed]);

  // Reset cursor when voci identity changes
  useEffect(() => {
    setCursor(0);
  }, [session?.session_id]);

  const head = queue[cursor] || null;
  const total = queue.length;

  const advance = useCallback(() => {
    setCursor((c) => c + 1);
  }, []);

  const dismiss = useCallback(() => {
    if (!head) return;
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(head.key);
      return next;
    });
    // cursor unchanged: queue shrinks on next render
  }, [head]);

  const resetDismissed = useCallback(() => {
    setDismissed(new Set());
    setCursor(0);
  }, []);

  return {
    /** Current hint to show, or null if queue is empty */
    head,
    /** Number of pending hints (excluding dismissed) */
    pending: total,
    /** Call after a confirm to move to the next hint */
    advance,
    /** Soft-skip the current hint (stays until page reload / reset) */
    dismiss,
    /** Force re-open all dismissed */
    resetDismissed,
  };
}
